import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { isoToday, startOfWeekISO } from 'sectors/pilates/sessions/data/mockCalendarData';
import { useWeeklySessions } from 'sectors/pilates/sessions/hooks/useWeeklySessions';

import ReportsBarChart from 'examples/Charts/BarCharts/ReportsBarChart';
import ReportsLineChart from 'examples/Charts/LineCharts/ReportsLineChart';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';

import PilatesTodaySessionsCard from '../dashboard/components/PilatesTodaySessionsCard';

import QuickActionsPanel from 'shared/ui/QuickActions/QuickActionsPanel';
import { pilatesQuickActions } from '../dashboard/pilatesQuickActions';

import { useDashboardOverview } from 'sectors/pilates/dashboard/hooks/useDashboardOverview';
import TodaySessionsModal from '../sessions/components/TodaySessionsModal';
import TakePaymentModal from '../payments/modal/TakePaymentModal';
import { MemberCreateModal } from '../members/modal/MemberCreateModal/MemberCreateModal';
import MonthPaymentsModal from '../dashboard/modals/MonthPaymentsModal';
import MonthNewMembersModal from '../dashboard/modals/MonthNewMembersModal';
import ExpectedPaymentsModal from '../dashboard/modals/ExpectedPaymentsModal';
import { accountingApi } from '../accounting/api/accountingApi';
import { staffApi } from '../staff/api/staffApi';

function trMoney(v) {
  const n = Number(v || 0);
  return `₺${n.toLocaleString('tr-TR')}`;
}

function ymLabel(ym) {
  return ym;
}

function toLineChart(items = [], { label = 'Deger' } = {}) {
  const labels = items.map((x) => ymLabel(x.ym));
  const data = items.map((x) => Number(x.value || 0));
  return { labels, datasets: { label, data } };
}

function toBarChart(items = [], { label = 'Adet' } = {}) {
  const labels = items.map((x) => ymLabel(x.ym));
  const data = items.map((x) => Number(x.value || 0));
  return { labels, datasets: { label, data } };
}

function formatDateISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getCurrentMonthRange() {
  const now = new Date();
  return {
    from: formatDateISO(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: formatDateISO(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
}

function TrainerCollectionCard({ trainers = [] }) {
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [summary, setSummary] = useState({
    totalCollectedTry: 0,
    expectedPaymentTry: 0,
  });

  useEffect(() => {
    if (!selectedTrainerId && trainers.length > 0) {
      setSelectedTrainerId(String(trainers[0].id));
    }
  }, [selectedTrainerId, trainers]);

  useEffect(() => {
    if (!selectedTrainerId) return;
    let cancelled = false;
    const range = getCurrentMonthRange();

    (async () => {
      try {
        const res = await accountingApi.income({
          from: range.from,
          to: range.to,
          trainerId: Number(selectedTrainerId),
          limit: 200,
        });

        if (!cancelled) {
          setSummary({
            totalCollectedTry: Number(res?.summary?.totalCollectedTry || 0),
            expectedPaymentTry: Number(res?.summary?.expectedPaymentTry || 0),
          });
        }
      } catch (error) {
        if (!cancelled) {
          setSummary({
            totalCollectedTry: 0,
            expectedPaymentTry: 0,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedTrainerId]);

  return (
    <Card>
      <MDBox p={3}>
        <MDBox
          display="flex"
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
        >
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold">
              Egitmene Gore Tahsilat
            </MDTypography>
            <MDTypography variant="button" color="text" sx={{ opacity: 0.75 }}>
              Secili egitmenin bu ay aldigi ve almasi gereken odemeler
            </MDTypography>
          </MDBox>

          <TextField
            select
            size="small"
            value={selectedTrainerId}
            onChange={(e) => setSelectedTrainerId(e.target.value)}
            sx={{ minWidth: { xs: '100%', md: 260 } }}
          >
            {trainers.map((trainer) => (
              <MenuItem key={trainer.id} value={String(trainer.id)}>
                {trainer.fullName || `#${trainer.id}`}
              </MenuItem>
            ))}
          </TextField>
        </MDBox>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}>
            <ComplexStatisticsCard
              color="success"
              icon="payments"
              title="Alinan odeme"
              count={trMoney(summary.totalCollectedTry)}
              percentage={{ color: 'success', amount: '', label: 'bu ay' }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ComplexStatisticsCard
              color="warning"
              icon="pending_actions"
              title="Alinmasi gereken"
              count={trMoney(summary.expectedPaymentTry)}
              percentage={{ color: 'warning', amount: '', label: 'bekleyen' }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ComplexStatisticsCard
              color="info"
              icon="insights"
              title="Tahmini toplam"
              count={trMoney(summary.totalCollectedTry + summary.expectedPaymentTry)}
              percentage={{ color: 'info', amount: '', label: 'bu ay' }}
            />
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default function PilatesDashboard() {
  const nav = useNavigate();

  const [trainerScope] = useState('ME');
  const [trainerId] = useState(null);
  const [sessionsPeriod] = useState('TODAY');
  const [months] = useState(6);

  const [todayOpen, setTodayOpen] = useState(false);
  const [takePaymentOpen, setTakePaymentOpen] = useState(false);
  const [createMemberOpen, setCreateMemberOpen] = useState(false);
  const [newMembersOpen, setNewMembersOpen] = useState(false);
  const [expectedOpen, setExpectedOpen] = useState(false);
  const [monthPaymentsOpen, setMonthPaymentsOpen] = useState(false);
  const [trainerOptions, setTrainerOptions] = useState([]);

  const openCreateMember = useCallback(() => setCreateMemberOpen(true), []);
  const closeCreateMember = useCallback(() => setCreateMemberOpen(false), []);

  const onSaveMember = useCallback(async (dto) => {
    console.log('create member dto', dto);
  }, []);

  const { data, isLoading, isError } = useDashboardOverview({
    trainerScope,
    trainerId,
    sessionsPeriod,
    months,
    day: null,
  });

  const k = data?.kpis || {};
  const today = data?.today || {};
  const todaySummary = today?.summary || {};
  const charts = data?.charts || {};

  const monthCollectedTry = k.monthCollectedTry ?? 0;
  const monthNewMembers = k.monthNewMembers ?? 0;
  const expectedPaymentTry = k.expectedPaymentTry ?? 0;
  const plannedSessions = todaySummary.planned ?? k.todaySessionsCount ?? 0;
  const loadingText = '...';

  const chartNewMembers = useMemo(
    () => toBarChart(charts.newMembersByMonth || [], { label: 'Yeni uye' }),
    [charts.newMembersByMonth]
  );

  const chartPayments = useMemo(
    () => toLineChart(charts.paymentsByMonth || [], { label: 'Tahsilat' }),
    [charts.paymentsByMonth]
  );

  const chartPassiveMembers = useMemo(
    () => toLineChart(charts.passiveMembersByMonth || [], { label: 'Pasif uye' }),
    [charts.passiveMembersByMonth]
  );

  const todayISO = isoToday();
  const todayWeekStartISO = useMemo(() => startOfWeekISO(todayISO), [todayISO]);
  const { sessions: weeklySessions, fetchWeekly } = useWeeklySessions(todayWeekStartISO);

  useEffect(() => {
    if (todayOpen) fetchWeekly();
  }, [todayOpen, fetchWeekly]);

  useEffect(() => {
    let cancelled = false;

    staffApi
      .list()
      .then((list) => {
        if (!cancelled) {
          setTrainerOptions(Array.isArray(list) ? list : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTrainerOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const actions = useMemo(
    () =>
      pilatesQuickActions({
        openTodaySessions: () => setTodayOpen(true),
        openTakePayment: () => setTakePaymentOpen(true),
        openCreateMember: () => setCreateMemberOpen(true),
      }),
    []
  );

  return (
    <MDBox py={3}>
      <MDBox mb={2}>
        {isError ? (
          <MDTypography variant="button" color="error">
            Dashboard verisi alinamadi.
          </MDTypography>
        ) : null}
      </MDBox>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={5} sx={{ cursor: 'pointer' }} onClick={() => setMonthPaymentsOpen(true)}>
            <ComplexStatisticsCard
              color="info"
              icon="payments"
              title="Bu ay tahsilat"
              count={isLoading ? loadingText : trMoney(monthCollectedTry)}
              percentage={{ color: 'success', amount: '', label: 'alinan odeme' }}
            />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5} sx={{ cursor: 'pointer' }} onClick={() => setNewMembersOpen(true)}>
            <ComplexStatisticsCard
              color="warning"
              icon="person_add"
              title="Yeni uye"
              count={isLoading ? loadingText : String(monthNewMembers)}
              percentage={{ color: 'warning', amount: '', label: 'bu ay eklenen' }}
            />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MDBox
            mb={1.5}
            sx={{ cursor: 'pointer', '&:active': { transform: 'scale(0.99)' } }}
            onClick={() => setTodayOpen(true)}
          >
            <ComplexStatisticsCard
              color="success"
              icon="event_available"
              title="Bugunku seans"
              count={isLoading ? loadingText : String(plannedSessions)}
              percentage={{ color: 'success', amount: '', label: 'planlanan' }}
            />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5} sx={{ cursor: 'pointer' }} onClick={() => setExpectedOpen(true)}>
            <ComplexStatisticsCard
              color="dark"
              icon="pending_actions"
              title="Alinmasi gereken odeme"
              count={isLoading ? loadingText : trMoney(expectedPaymentTry)}
              percentage={{ color: 'error', amount: '', label: 'acik bakiye' }}
            />
          </MDBox>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={3}>
            <ReportsBarChart
              color="info"
              title="Yeni uye"
              description="Aylik kayit hizi"
              date={`${months} ay`}
              chart={chartNewMembers}
            />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={3}>
            <ReportsLineChart
              color="success"
              title="Tahsilat"
              description={`Son ${months} ay`}
              date={`${data?.context?.monthFrom ?? ''} -> ${data?.context?.monthTo ?? ''}`}
              chart={chartPayments}
            />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={3}>
            <ReportsLineChart
              color="dark"
              title="Pasif uye"
              description="Aylik pasiflesme"
              date={`${months} ay`}
              chart={chartPassiveMembers}
            />
          </MDBox>
        </Grid>
      </Grid>

      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PilatesTodaySessionsCard
              isLoading={isLoading}
              summary={todaySummary}
              sessions={today?.sessions || []}
            />
          </Grid>
        </Grid>
      </MDBox>

      <QuickActionsPanel
        title="Hizli islemler"
        subtitle="Gunluk operasyon"
        actions={actions}
        bottomButtons={[
          {
            key: 'members',
            label: 'Uyeler',
            color: 'info',
            onClick: () => nav('/app/pilates/members'),
          },
          {
            key: 'sessions',
            label: 'Takvim',
            color: 'dark',
            onClick: () => nav('/app/pilates/sessions'),
          },
        ]}
      />

      <MDBox mt={3}>
        <TrainerCollectionCard trainers={trainerOptions} />
      </MDBox>

      <TodaySessionsModal
        open={todayOpen}
        onClose={() => setTodayOpen(false)}
        todayISO={todayISO}
        sessions={weeklySessions || []}
        trainers={data?.trainers || []}
      />

      <TakePaymentModal open={takePaymentOpen} onClose={() => setTakePaymentOpen(false)} />

      <MemberCreateModal
        open={createMemberOpen}
        onClose={closeCreateMember}
        onSave={onSaveMember}
      />

      <MonthPaymentsModal
        open={monthPaymentsOpen}
        onClose={() => setMonthPaymentsOpen(false)}
        items={data?.kpis?.monthPaymentsByPayer || []}
        totalTry={data?.kpis?.monthCollectedTry || 0}
        monthFrom={data?.context?.monthFrom}
        monthTo={data?.context?.monthTo}
        onSelectMember={(row) => {
          console.log('select member month payments row', row);
        }}
      />

      <MonthNewMembersModal
        open={newMembersOpen}
        onClose={() => setNewMembersOpen(false)}
        items={data?.kpis?.monthNewMemberItems || []}
        monthFrom={data?.context?.monthFrom}
        monthTo={data?.context?.monthTo}
        onSelectMember={(row) => {
          console.log('select new member', row);
        }}
      />

      <ExpectedPaymentsModal
        open={expectedOpen}
        onClose={() => setExpectedOpen(false)}
        items={data?.kpis?.expectedPaymentItems || []}
        totalTry={data?.kpis?.expectedPaymentTry || 0}
        monthFrom={data?.context?.monthFrom}
        monthTo={data?.context?.monthTo}
        onSelectMember={(row) => {
          console.log('select expected payment member', row);
        }}
      />
    </MDBox>
  );
}
